import {
  B5,
  Badge,
  Box,
  Button,
  DropdownMenu,
  H3,
  LinkButton,
  P,
  Text
} from "@adfenix/alexandria";
import { Col, Row } from "react-grid-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteProgram, duplicateProgram } from "api/services/programService";

import Card from "components/Card";
import { Collapse } from "react-collapse";
import Divider from "components/Divider";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";

import UilEllipsis from "@iconscout/react-unicons/icons/uil-ellipsis-h";
import UilPlay from "@iconscout/react-unicons/icons/uil-play-circle";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useCheckPermission } from "hooks/usePermissions";
import { AppMode } from "../details";
import { ProgramService } from "@adfenix/types";
import { customerFacingPublishingMode } from "../../../../utils/publishingMode";
import { sortOrder } from ".";
import { useTranslation } from "react-i18next";
import { Timestamp } from "components/Timestamp";

const IconWithMargin = styled.i`
  margin-right: 4px;
  margin-left: 4px;
  width: 20px;
`;

const ClickableRow = styled(Row)`
  cursor: pointer;
`;

export const OverviewItem = ({
  item,
  appMode
}: {
  item: ProgramService.ProgramSearchModel;
  appMode: AppMode;
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { hqId } = useParams<HqIdRouteParams>();
  const [isOpen, setIsOpen] = useState(false);
  const [showAllOffices, setShowAllOffices] = useState(false);

  const hasAdminPermission = useCheckPermission({
    scope: "Admin",
    action: "Edit"
  });

  const hasEditPermission = useCheckPermission({
    scope: "Program",
    action: "Edit"
  });

  const queryClient = useQueryClient();

  const {
    mutateAsync: triggerDuplicateProgram,
    isLoading: duplicatingProgram
  } = useMutation(duplicateProgram);

  const { mutateAsync: triggerDeleteProgram, isLoading: deletingProgram } =
    useMutation(deleteProgram);

  const sortOffices = (
    offices: ProgramService.NamedEntityReadLayerModel[]
  ): ProgramService.NamedEntityReadLayerModel[] => {
    return offices.sort(
      (
        a: ProgramService.NamedEntityReadLayerModel,
        b: ProgramService.NamedEntityReadLayerModel
      ) => a.name.localeCompare(b.name)
    );
  };

  return (
    <Card style={{ padding: 16 }}>
      <Col>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <H3>{item.name}</H3>
            id: {item.id}
          </Box>

          <Box display="flex" alignItems="center">
            {
              <Box pX="m">
                {item.active ? (
                  <Badge color="success">{"Active"}</Badge>
                ) : (
                  <Badge color="grey">{"Inactive"}</Badge>
                )}
              </Box>
            }

            <Box display="flex" alignItems="center">
              <LinkButton
                logLabel="program-link-button"
                data-testid={`program-link-button-${item.name}`}
                size="medium"
                to={`/${appMode}/hq/${hqId}/program-builder/${item.id}`}
              >
                {t("programBuilder.program.overview.item.button.details")}
              </LinkButton>

              <Box pX={"xs"}>
                <DropdownMenu
                  logLabelSuffix="program-overview-item-actions"
                  items={{
                    Actions: [
                      {
                        text: t(
                          "programBuilder.program.overview.item.actions.duplicate.label"
                        ),

                        disabled: duplicatingProgram || !hasEditPermission,
                        callback: async () => {
                          const shouldDuplicate = window.confirm(
                            t(
                              "programBuilder.program.overview.item.actions.duplicate.confirmation.message"
                            )
                          );

                          if (shouldDuplicate) {
                            try {
                              const newProgramId =
                                await triggerDuplicateProgram({
                                  programId: item.id,
                                  hqId: hqId!
                                });

                              toast.success(
                                t(
                                  "programBuilder.program.overview.item.actions.duplicate.toast.success"
                                )
                              );
                              queryClient.invalidateQueries([
                                "programOverview"
                              ]);
                              navigate(
                                `/${appMode}/hq/${hqId}/program-builder/${newProgramId}`
                              );
                            } catch {
                              toast.error(
                                t(
                                  "programBuilder.program.overview.item.actions.duplicate.toast.error"
                                )
                              );
                            }
                          }
                        },
                        icon: "copy"
                      },
                      {
                        text: t(
                          "programBuilder.program.overview.item.actions.delete.label"
                        ),

                        disabled: deletingProgram || !hasAdminPermission,
                        callback: async () => {
                          const shouldRemove = window.confirm(
                            t(
                              "programBuilder.program.overview.item.actions.delete.confirmation.message",
                              { value: item.name }
                            ) +
                              t(
                                "programBuilder.program.overview.item.actions.delete.confirmation.details"
                              )
                          );
                          if (shouldRemove) {
                            try {
                              await triggerDeleteProgram({
                                programId: item.id
                              });
                              toast.success(
                                t(
                                  "programBuilder.program.overview.item.actions.delete.toast.success"
                                )
                              );
                              queryClient.invalidateQueries([
                                "programOverview"
                              ]);
                            } catch {
                              toast.error(
                                t(
                                  "programBuilder.program.overview.item.actions.delete.toast.error"
                                )
                              );
                            }
                          }
                        },
                        icon: "trash-alt"
                      }
                    ]
                  }}
                >
                  <UilEllipsis size="20px" />
                </DropdownMenu>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider margin="16px -16px" />

        <ClickableRow
          onClick={() => {
            setIsOpen(prev => !prev);
          }}
        >
          <Col xs={4}>
            <Box display="flex" alignItems="center">
              <UilPlay style={{ marginRight: 8 }} size="20px" />
              <B5 color="grey">
                {sortOrder.includes(item.triggerEvent)
                  ? item.triggerEvent
                  : t(
                      "programBuilder.program.overview.item.triggerEvent.custom"
                    )}
              </B5>
            </Box>
          </Col>

          <Col xs={4}>
            <B5 color="grey">
              {customerFacingPublishingMode(item.publishingMode, t)}
            </B5>
          </Col>

          <Col xs={4}>
            {item && item.officeMode === "ActiveOnSelectedOffices" ? (
              <>
                <B5 color="grey">
                  {item.selectedOffices.length > 1
                    ? t(
                        "programBuilder.program.overview.item.officeMode.offices"
                      )
                    : t(
                        "programBuilder.program.overview.item.officeMode.office"
                      )}

                  {item.selectedOffices.length == 1
                    ? item.selectedOffices[0].name
                    : item.selectedOffices.length}
                </B5>
              </>
            ) : (
              <>
                <B5 color="grey">
                  {item.selectedOffices.length == 1 &&
                    t(
                      "programBuilder.program.overview.item.officeMode.allOfficesExcept"
                    )}

                  {item.selectedOffices.length == 1 &&
                    item.selectedOffices[0].name}

                  {item.selectedOffices.length == 0 &&
                    t(
                      "programBuilder.program.overview.item.officeMode.allOffices"
                    )}

                  {item.selectedOffices.length > 1 &&
                    item.selectedOffices.length +
                      t(
                        "programBuilder.program.overview.item.officeMode.excludedOffices"
                      )}
                </B5>
              </>
            )}
            <Text color="primary">
              <IconWithMargin
                className={
                  isOpen
                    ? "uil uil-angle-up is-size-5"
                    : "uil uil-angle-down is-size-5"
                }
              />
            </Text>
          </Col>
        </ClickableRow>

        <Collapse isOpened={isOpen}>
          <Divider margin="16px -16px" />

          <Box display="flex" justifyContent="space-between">
            <Box style={{ flexGrow: 1 }}>
              <B5>
                {t("programBuilder.program.overview.item.moreInfo.header")}
              </B5>
              <Box
                style={{ paddingTop: 0 }}
                display="flex"
                flexDirection="column"
                pY="xs2"
              >
                <Text color="grey">
                  {t("programBuilder.program.overview.item.created")}
                </Text>

                <Timestamp timestamp={item.createdAt} />
              </Box>

              <Box display="flex" flexDirection="column" pY="xs2">
                <Text color="grey">Bundle groups</Text>
                {item?.bundleGroupNames?.map((name: string) => (
                  <P key={name}>{name}</P>
                ))}
              </Box>

              {item?.ownedByUser && (
                <Box display="flex" flexDirection="column" pY="xs2">
                  <Text color="grey">
                    {t("programBuilder.program.overview.item.programOwner")}
                  </Text>
                  <P>
                    {item.ownedByUser.email} - {item.ownedByUser.name}
                  </P>
                </Box>
              )}
            </Box>
            <Box style={{ flexGrow: 1 }}>
              <B5>Paid by:</B5>
              <Box
                style={{ paddingTop: 0 }}
                display="flex"
                flexDirection="column"
                pY="xs2"
              >
                {item?.paidBy.map(x => (
                  <P key={x}>{x}</P>
                ))}
              </Box>
            </Box>

            <Box style={{ flexGrow: 1 }}>
              <Badge
                lightness="lighter"
                color={
                  item.officeMode === "ActiveOnAllOfficesExceptSelected"
                    ? "danger"
                    : "success"
                }
              >
                {item.officeMode === "ActiveOnAllOfficesExceptSelected"
                  ? t(
                      "programBuilder.program.details.selectOffice.officeMode.excludedOffices.label"
                    )
                  : t(
                      "programBuilder.program.details.selectOffice.officeMode.includedOffices.label"
                    )}
              </Badge>

              {sortOffices(item.selectedOffices)
                .slice(0, 5)
                .map((office: ProgramService.NamedEntityReadLayerModel) => (
                  <P key={office.id}>{office.name}</P>
                ))}

              {showAllOffices &&
                sortOffices(item.selectedOffices)
                  .slice(5)
                  .map((office: ProgramService.NamedEntityReadLayerModel) => (
                    <P key={office.id}>{office.name}</P>
                  ))}

              {!showAllOffices && (
                <Button
                  logLabel="show-all-offices"
                  variant="text"
                  onClick={() => setShowAllOffices(true)}
                >
                  {item.selectedOffices && item.selectedOffices.length > 5 && (
                    <>{`${sortOffices(item.selectedOffices).length - 5} ${t(
                      "programBuilder.program.overview.offices.button.showMore"
                    )}
                    `}</>
                  )}
                </Button>
              )}

              {showAllOffices && (
                <Button
                  logLabel="dont-show-all-offices"
                  variant="text"
                  onClick={() => setShowAllOffices(false)}
                >
                  {t("programBuilder.program.overview.offices.button.hide")}
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>
      </Col>
    </Card>
  );
};
